import { Skeleton } from '@mui/material';
import React, { FC } from 'react';
import styled from 'styled-components';
import {
  OvulatoryEventDayRow,
  OvulatoryEventLoadStatus,
} from '../../../common/model/type/ovulatory-event-prediction.type';
import {
  formatOvulatoryEventDay,
  formatOvulatoryEventScoredAt,
  formatOvulatoryEventValue,
} from '../../../common/utils/services/ovulatory-event-prediction-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import {
  getOvulatoryEventIneligibilityReasonLabel,
  OVULATORY_EVENT_LIMITED_EVIDENCE_DESCRIPTION,
  OVULATORY_EVENT_OUTCOME_DESCRIPTIONS,
} from '../organisms/ovulatory-event-prediction-presentation';

interface OvOvulatoryEventDayDetailsProps {
  status: OvulatoryEventLoadStatus;
  row: OvulatoryEventDayRow;
}

const OvOvulatoryEventDayDetails: FC<OvOvulatoryEventDayDetailsProps> = ({
  status,
  row: { comparison, prediction },
}) => {
  const { model, observed_outcome, observed_event_day } = comparison;
  const inputs = Object.entries(prediction?.features ?? {});

  return (
    <Wrapper>
      <Header>
        <Title>Day details</Title>
        {prediction && (
          <Meta>
            {prediction.model_version} · Manifest {prediction.manifest_version}{' '}
            · Scored{' '}
            <time dateTime={prediction.updated_at}>
              {formatOvulatoryEventScoredAt(prediction.updated_at)}
            </time>
          </Meta>
        )}
      </Header>

      <Facts>
        <span>
          Observed outcome:{' '}
          {OVULATORY_EVENT_OUTCOME_DESCRIPTIONS[observed_outcome]}
        </span>
        <span>
          Observed event day:{' '}
          {observed_event_day
            ? formatOvulatoryEventDay(observed_event_day)
            : '—'}
        </span>
        {model.evidence_level === 'limited' && (
          <span>{OVULATORY_EVENT_LIMITED_EVIDENCE_DESCRIPTION}</span>
        )}
        {prediction && (
          <>
            <span>
              Ineligibility reason:{' '}
              {prediction.state === 'ineligible'
                ? getOvulatoryEventIneligibilityReasonLabel(
                    prediction.ineligibility_reason
                  )
                : 'Not applicable'}
            </span>
            <span>
              Approximate:{' '}
              {prediction.backfilled ? 'Yes · Rule values only' : 'No'}
            </span>
          </>
        )}
      </Facts>

      {status === 'loading' && <Skeleton variant="rounded" height="4rem" />}

      {status === 'error' && (
        <Message>
          The stored prediction could not be loaded, so its provenance and
          inputs are unavailable.
        </Message>
      )}

      {status === 'ready' && !prediction && (
        <Message>No stored prediction was returned for this row.</Message>
      )}

      {status === 'ready' && prediction && !prediction.features && (
        <Message>No inputs: an ineligible row is not scored.</Message>
      )}

      {status === 'ready' && inputs.length > 0 && (
        <>
          <Header>
            <Title>{inputs.length} inputs</Title>
            <Meta>— Not available; never treated as zero</Meta>
          </Header>
          <Inputs>
            {inputs.map(([name, value]) => (
              <Input key={name}>
                <dt>{name}</dt>
                <dd>
                  {value === null ? '—' : formatOvulatoryEventValue(value)}
                </dd>
              </Input>
            ))}
          </Inputs>
        </>
      )}
    </Wrapper>
  );
};

export default OvOvulatoryEventDayDetails;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.875rem 1rem 1rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
  background: ${Colours.OV_WHITE};
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem 1.5rem;
`;

const Title = styled.h4`
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
`;

const Meta = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-variant-numeric: tabular-nums;
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem 2.5rem;
  color: ${Colours.OV_CHARCOAL};
`;

const Message = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;

const Inputs = styled.dl`
  columns: 5 12rem;
  column-gap: 2rem;
  margin: 0;
`;

const Input = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.1875rem 0;
  break-inside: avoid;

  dt {
    min-width: 0;
    color: ${Colours.OV_CHARCOAL};
    overflow-wrap: anywhere;
  }

  dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
  }
`;
